import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysDataSubscription } from './queryKeys';
import { type MutateNotificationPolicyData } from './types';

type DataSubscriptionResponse = {
  code: number;
  message: string;
  requestId: UUID | undefined;
};

/**
 * Deletes a notification policy.
 *
 * @param {UUID} contactPointId - An uuid containing the notification policy details to be deleted.
 * @param {UUID} dataSubscriptionId - An uuid containing the data subscription details to be deleted.
 * @returns {Promise<DataSubscriptionResponse>} - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Subscription/operation/create_notification_policy_data_subscription_notification_policy_post
 */
export const deleteNotificationPolicy = async (
  contactPointId: UUID,
  dataSubscriptionId: UUID,
) => {
  const response = await vestigasApi
    .post(ENDPOINT.DATA_SUBSCRIPTION.DELETE_NOTIFICATION_POLICY(), {
      json: {
        contactPointId,
        dataSubscriptionId,
      },
    })
    .json<DataSubscriptionResponse>();

  return response;
};

/**
 * Custom hook for deleting a notification policy using mutations.
 *
 * @returns {Object} An object containing the mutation function and other properties.
 */
export const useMutationDeleteNotificationPolicy = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      contactPointId,
      dataSubscriptionId,
    }: MutateNotificationPolicyData) =>
      deleteNotificationPolicy(contactPointId, dataSubscriptionId),
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: queryKeysDataSubscription.getAllContactPoints(),
      });
    },
  });
};
